'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import CredentialsHeader from './components/CredentialsHeader';
import ComplianceDashboard from './components/ComplianceDashboard';
import LicenseInventory from './components/LicenseInventory';
import RenewalAlerts from './components/RenewalAlerts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { credentialsService, type Credential, type CredentialStatus } from '@/lib/services/staffingService';

export type CredentialsView = 'dashboard' | 'inventory' | 'alerts';
export type { CredentialStatus };
export type { Credential };

export interface StaffCompliance {
  staffId: string;
  staffName: string;
  role: string;
  department: string;
  totalCredentials: number;
  activeCount: number;
  expiringSoonCount: number;
  expiredCount: number;
  pendingCount: number;
  complianceScore: number;
}

export default function CredentialsPage() {
  const [view, setView] = useState<CredentialsView>('dashboard');
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<CredentialStatus | 'all'>('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterCategory, setFilterCategory] = useState<Credential['category'] | 'all'>('all');

  const loadCredentials = useCallback(async () => {
    try {
      const data = await credentialsService.getAll();
      setCredentials(data);
    } catch {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCredentials();
    const unsub = credentialsService.subscribe(
      (c) => setCredentials((prev) => [...prev, c]),
      (c) => setCredentials((prev) => prev.map((x) => (x.id === c.id ? c : x))),
      (id) => setCredentials((prev) => prev.filter((x) => x.id !== id)),
    );
    return unsub;
  }, [loadCredentials]);

  const filtered = useMemo(() => {
    return credentials.filter((c) => {
      const matchSearch =
        !search ||
        c.staffName.toLowerCase().includes(search.toLowerCase()) ||
        c.credentialName.toLowerCase().includes(search.toLowerCase()) ||
        c.licenseNumber.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchDept = filterDept === 'all' || c.department === filterDept;
      const matchCat = filterCategory === 'all' || c.category === filterCategory;
      return matchSearch && matchStatus && matchDept && matchCat;
    });
  }, [credentials, search, filterStatus, filterDept, filterCategory]);

  const alertCredentials = useMemo(
    () => credentials.filter((c) => c.status === 'expired' || c.status === 'expiring_soon').sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry),
    [credentials]
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground text-sm">Loading credentials…</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <CredentialsHeader
        view={view}
        onViewChange={setView}
        totalExpired={credentials.filter((c) => c.status === 'expired').length}
        totalExpiringSoon={credentials.filter((c) => c.status === 'expiring_soon').length}
      />

      {view === 'dashboard' && (
        <ComplianceDashboard credentials={credentials} />
      )}

      {view === 'inventory' && (
        <LicenseInventory
          credentials={filtered}
          search={search}
          onSearchChange={setSearch}
          filterStatus={filterStatus}
          onFilterStatus={setFilterStatus}
          filterDept={filterDept}
          onFilterDept={setFilterDept}
          filterCategory={filterCategory}
          onFilterCategory={setFilterCategory}
        />
      )}

      {view === 'alerts' && (
        <RenewalAlerts credentials={alertCredentials} />
      )}
    </AppLayout>
  );
}
